import React from 'react';
import { Box, Card, CardContent, Skeleton, Tooltip } from '@mui/material';
import { Typography } from '@mui/joy';
import { telemetryIconFactory } from '../../utils/telemetryIconFactory';

interface Props {
    name: string;
    value: number | null;
    unitSymbol?: string;
    unitName?: string;
    description?: string;
    loading?: boolean;
}

const TelemetryAttributeCard = ({ name, value, unitSymbol, unitName, description, loading }: Props) => {
    const formatValue = (val: number | null): string => {
        if (val === null || val === undefined) return "no data";
        return val.toFixed(2);
    };

    if (loading) {
        return (
            <Card sx={{
                border: "1px solid #cccccc",
                borderRadius: "4px",
                height: "126px",
                minWidth: "400px",
                display: "flex",
                flexDirection: "column"
            }}>
                <CardContent sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                    padding: "20px",
                    height: "100%"
                }}>
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Skeleton variant="text" width="80%" height={30} />
                        <Skeleton variant="text" width="60%" height={40} />
                    </Box>
                    <Skeleton variant="circular" width={70} height={70} />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{
            border: "1px solid #cccccc",
            borderRadius: "4px",
            height: "126px",
            minWidth: "400px",
            display: "flex",
            flexDirection: "column",
            boxShadow: "none"
        }}>
            <CardContent sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                padding: "20px",
                height: "100%",
                "&:last-child": {
                    paddingBottom: "20px"
                }
            }}>
                {/* Left side - Title and Value */}
                <Box sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                    overflow: "hidden"
                }}>
                    {/* Title */}
                    <Tooltip title={name} arrow placement="top">
                        <Typography
                            level="titleMediumLight"
                            sx={{
                                fontWeight: 400,
                                color: "#333333",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                        >
                            {name}
                        </Typography>
                    </Tooltip>

                    {/* Value Display */}
                    <Box sx={{paddingLeft: "12px"}}>
                        <Typography
                        level="titleExtraLarge"
                            sx={{
                                fontWeight: 400,
                                color: "#333333"
                            }}
                        >
                            {value !== null ? `${formatValue(value)}${unitSymbol || ''}` : "—"}
                        </Typography>
                        {value === null && (
                            <Typography level="bodySmall" sx={{ color: "#999" }}>
                                no data
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Right side - Icon */}
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "8px",
                    height: "100%"
                }}>
                    <Box sx={{ width: "70px", height: "70px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {telemetryIconFactory(unitSymbol, 70)}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default TelemetryAttributeCard;
